import React, { SetStateAction, useEffect, useState } from "react";
import {
  Autocomplete,
  Fade,
  Paper,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { DeviceHub } from "@mui/icons-material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { UserOrEmailMapping } from "../../features/Admin/typings/admin.types";
import { setInputs } from "../../features/Booking-Form/slices/booking.slice";
import { getOptionLabelOfUserOrEmailMapping } from "../../utils/type/type.utils";
import FormRecurrence from "./FormRecurrence/form-recurrence.component";
import FormDateTimeInventoryFilters from "../../features/Booking-Form/partials/form-date-time-inventory-filters.partial";
import {
  handleConference,
  initBookingTime,
  setDatesAndValidate
} from "./functions/date-time.functions";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import { calculateMaxEndDate, updateInitialBookingTimeByNow } from "./utils/booking.utils";

type Props = {
  setValidDates: React.Dispatch<SetStateAction<boolean>>;
  validDate: boolean;
};

/**
 * @description Component to select date and time for the booking process in two modes single or schedule builder.
 */
const FormDateTime: React.FC<Props> = ({ setValidDates, validDate }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { inputs } = useSelector((state: RootState) => state.booking);
  const {
    settings,
    userInformation: {
      sub,
      company: { meta: metaData }
    }
  } = useSelector((state: RootState) => state.login);
  const dispatch = useDispatch();

  const [message, setMessage] = useState<string>();
  const [users, setUsers] = useState<UserOrEmailMapping[]>([]);

  const activityBasedBookingTooltipDesc = t("_automatedBookingTooltipForConferenceRoom");

  /**
   * @description Takes the input Dates and Times and verifies them according to business logic.
   * @version 0.1.2
   */
  useEffect(() => {
    const maxDate = calculateMaxEndDate({ metaData, bookingMode: inputs.mode });
    setDatesAndValidate(inputs, setMessage, setValidDates, maxDate);
  }, [inputs, setValidDates, i18n.language, metaData]);

  useEffect(() => {
    handleConference(inputs, dispatch, users, setUsers, sub);
  }, [inputs, dispatch, users, setUsers, sub]);

  useEffect(() => {
    const todayDate = DateTime.now().toFormat("yyyy-MM-dd");

    // Set default times initially if they are not set
    if (!inputs.bookingStart && !inputs.bookingEnd) {
      const [initBookingStart, initBookingEnd] = initBookingTime(
        settings.standardStartTime,
        settings.standardEndTime,
        inputs.mode
      );

      dispatch(
        setInputs({
          bookingStart: initBookingStart,
          bookingEnd: initBookingEnd,
          bookingFrom: todayDate,
          bookingTo: todayDate,
          specificDays: [todayDate]
        })
      );
    }
  }, [
    dispatch,
    inputs.bookingEnd,
    inputs.bookingStart,
    settings.standardEndTime,
    settings.standardStartTime
  ]);

  useEffect(() => {
    const maxDate = calculateMaxEndDate({ metaData, bookingMode: inputs.mode });

    // compare the booking inputs date/time with now/day and initialize the booking time
    updateInitialBookingTimeByNow(inputs, settings, maxDate, dispatch);
  }, [inputs.bookingFrom]);

  useEffect(() => {
    if (inputs.mode === BookingType.PLACEZONE) {
      dispatch(
        setInputs({
          bookingFrom: DateTime.now().plus({ days: 1 }).toFormat("yyyy-MM-dd"),
          bookingTo: DateTime.now().plus({ days: 7 }).toFormat("yyyy-MM-dd")
        })
      );
    }
  }, [dispatch, inputs.mode]);

  return (
    <>
      {inputs.mode === "conferencezone" && (
        <Tooltip
          enterTouchDelay={0}
          title={<Typography>{activityBasedBookingTooltipDesc}</Typography>}
          aria-label={activityBasedBookingTooltipDesc}
          sx={{ display: "none" }}
        >
          <ToggleButton
            style={{
              marginTop: 10,
              background: inputs.activityBasedBooking
                ? theme.palette.primary.main
                : theme.palette.background.default,
              color: inputs.activityBasedBooking ? "#ffffff" : theme.palette.text.primary
            }}
            onClick={() =>
              dispatch(
                setInputs({
                  activityBasedBooking: !inputs.activityBasedBooking
                })
              )
            }
            value="auto"
            selected={inputs.activityBasedBooking}
            aria-label="left aligned"
          >
            {t("Activity Based Booking")} <DeviceHub />
          </ToggleButton>
        </Tooltip>
      )}

      <Fade in={true} mountOnEnter unmountOnExit>
        <Paper
          data-testid={"form-dateTime-comp"}
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            flexDirection: "column",
            padding: theme.spacing(3),
            marginRight: theme.spacing(3),
            width: "100%",
            backgroundImage: "none"
          }}
        >
          <Typography variant={"h5"}>{t("Date and Time")}</Typography>
          <FormRecurrence />
          <FormDateTimeInventoryFilters validDate={validDate} />
        </Paper>
      </Fade>

      {/* error message for invalid date */}
      {!validDate && (
        <Typography
          sx={{ textAlign: "left", color: theme.palette.error.main, position: "relative" }}
          variant={"body2"}
        >
          {message}
        </Typography>
      )}

      {inputs.mode === "conferencezone" && inputs.activityBasedBooking && (
        <>
          <Autocomplete
            multiple
            disableCloseOnSelect
            id="tags-outlined9"
            isOptionEqualToValue={(option: UserOrEmailMapping, value: UserOrEmailMapping) =>
              option.email === value.email
            }
            getOptionLabel={getOptionLabelOfUserOrEmailMapping}
            data-testid={"autocomplete"}
            options={users}
            filterSelectedOptions
            style={{ width: "100%" }}
            onChange={(event, values) => {
              event.persist();
              dispatch(setInputs({ zoneAccess: values }));
            }}
            value={inputs.zoneAccess || []}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                fullWidth
                placeholder={t("Choose your colleagues")}
              />
            )}
          />

          <TextField
            margin="dense"
            id="name"
            label={t("Conference Subject")}
            type="text"
            value={inputs.description || ""}
            onChange={(event: any) => dispatch(setInputs({ description: event.target.value }))}
            fullWidth
          />
        </>
      )}
    </>
  );
};

export default FormDateTime;
